import { useState } from "react"
import { AlertTriangle, Loader2, CheckCircle, X, Wallet, Building } from 'lucide-react'
import { CopyButton, FileDropZone, InputCustom, SearchableSelect } from "../../../components"
import creditAccountsService from "../../../services/creditAccountsService"
import toast from "react-hot-toast"

const PLATFORM_OPTIONS = [
  { name: "Revolut Bank", value: "Revolut Bank" },
  { name: "Slash Bank", value: "Slash Bank" },
  { name: "Wise Bank", value: "Wise Bank" },
  { name: "Payoneer Bank", value: "Payoneer Bank" },
  { name: "Airwallex Bank", value: "Airwallex Bank" },
  { name: "Mercury Bank", value: "Mercury Bank" },
  { name: "Other", value: "Other" },
]

const BankTransferPayment = ({
  paymentMethods,
  formatCurrency,
  total,
  bankTransferReference,
  receiptFile,
  setReceiptFile,
  accountId,
  amountValue,
  currency,
  onSuccess,
  onClose,
  t = (text) => text,
}) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [error, setError] = useState(null)

  // Platform selector (SearchableSelect) + "Other" input (InputCustom)
  const [platformName, setPlatformName] = useState("Revolut Bank")
  const [customPlatformName, setCustomPlatformName] = useState("")

  // Confirmations
  const [confirmations, setConfirmations] = useState({
    reference: false,
    wireTransfer: false,
    fees: false,
  })
  const allConfirmed = confirmations.reference && confirmations.wireTransfer && confirmations.fees

  const handleSubmitPayment = async () => {
    if (!receiptFile) {
      toast.error(t("Please upload a receipt for bank transfer"))
      return
    }
    if (platformName === "Other" && !customPlatformName.trim()) {
      toast.error(t("Please enter the bank/platform name"))
      return
    }
    if (!allConfirmed) {
      toast.error(t("Please confirm all the checkboxes before submitting"))
      return
    }

    setIsProcessing(true)
    setError(null)

    try {
      const paymentData = {
        accountId: accountId,
        amount: amountValue,
        paymentMethod: "bankTransfer",
        currency: currency,
        receipt: receiptFile,
        reference: bankTransferReference,
        PlatformName: platformName === "Other" ? customPlatformName.trim() : platformName,

      }

      const response = await creditAccountsService.processBankTransferPayment(paymentData)

      if (response.data && response.data.statusCode === 200) {
        setIsComplete(true)
        // toast.success(t("Bank transfer receipt submitted successfully!"))
        onSuccess && onSuccess()

      } else {
        const msg = response.data?.message || t("Payment processing failed")
        toast.error(msg)
        setError(msg)
      }
    } catch (error) {
      console.error("Bank transfer error:", error)
      const msg = error.response?.data?.message || t("Payment processing failed. Please try again.")
      setError(msg)
      toast.error(msg)
    } finally {
      setIsProcessing(false)
    }
  }

  // Processing / Complete / Error states unchanged...
  if (isProcessing) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"></div>
        </div>
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-2">
          {t("Processing Bank Transfer")}
        </h3>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
          {t("Please wait while we upload your receipt...")}
        </p>
      </div>
    )
  }

  if (isComplete) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
          <CheckCircle size={24} className="text-green-500 dark:text-green-400" />
        </div>
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-2">
          {t("Receipt Submitted Successfully!")}
        </h3>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
          {t("Your bank transfer receipt has been submitted. Your account will be credited once the payment is verified.")}
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
          <X size={24} className="text-red-500 dark:text-red-400" />
        </div>
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-2">{t("Submission Failed")}</h3>
        <p className="text-xs text-red-500 dark:text-red-400 mb-4">{error}</p>
        <div className="flex flex-col gap-2 justify-center">
          <button
            onClick={() => setError(null)}
            className="px-4 py-2.5 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 text-xs font-medium"
          >
            {t("Try Again")}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="p-4 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-950/50 rounded-xl border border-neutral-200 dark:bg-neutral-800">
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-3">
          {t("Bank Transfer Instructions")}
        </h3>
        <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-3">
          {t("Please transfer")} {formatCurrency(total)} {t("to the following account")}:
        </p>
        <div className="space-y-2 mb-4 text-xs">
          {/* Existing info rows kept untouched */}
          <div className="flex justify-between items-center">
            <span className="text-neutral-600 dark:text-neutral-400">{t("Bank Name")}:</span>
            <div className="flex items-center gap-1">
              <span className="text-neutral-900 dark:text-neutral-200 font-medium">
                {paymentMethods.bankTransfer.bankName}
              </span>
              <CopyButton text={paymentMethods.bankTransfer.bankName} label="Copy bank name" />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-neutral-600 dark:text-neutral-400">{t("Account Name")}:</span>
            <div className="flex items-center gap-1">
              <span className="text-neutral-900 dark:text-neutral-200 font-medium">
                {paymentMethods.bankTransfer.accountName}
              </span>
              <CopyButton text={paymentMethods.bankTransfer.accountName} label="Copy account name" />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-neutral-600 dark:text-neutral-400">{t("Account Number")}:</span>
            <div className="flex items-center gap-1">
              <span className="text-neutral-900 dark:text-neutral-200 font-medium">
                {paymentMethods.bankTransfer.accountNumber}
              </span>
              <CopyButton text={paymentMethods.bankTransfer.accountNumber} label="Copy account number" />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-neutral-600 dark:text-neutral-400">{t("Routing Number")}:</span>
            <div className="flex items-center gap-1">
              <span className="text-neutral-900 dark:text-neutral-200 font-medium">
                {paymentMethods.bankTransfer.routingNumber}
              </span>
              <CopyButton text={paymentMethods.bankTransfer.routingNumber} label="Copy routing number" />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-neutral-600 dark:text-neutral-400">{t("SWIFT/BIC")}:</span>
            <div className="flex items-center gap-1">
              <span className="text-neutral-900 dark:text-neutral-200 font-medium">
                {paymentMethods.bankTransfer.swiftCode}
              </span>
              <CopyButton text={paymentMethods.bankTransfer.swiftCode} label="Copy SWIFT code" />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-neutral-600 dark:text-neutral-400">{t("Address")}:</span>
            <div className="flex items-center gap-1">
              <span className="text-neutral-900 dark:text-neutral-200 font-medium">
                {paymentMethods.bankTransfer.address}
              </span>
              <CopyButton text={paymentMethods.bankTransfer.address} label="Copy address" />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-neutral-600 dark:text-neutral-400">{t("Client ID")}:</span>
            <div className="flex items-center gap-1">
              <span className="text-neutral-900 dark:text-neutral-200 font-medium">{bankTransferReference}</span>
              <CopyButton text={bankTransferReference} label="Copy reference" />
            </div>
          </div>
        </div>

        {paymentMethods.bankTransfer?.additionalInfo && (
          <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-xl mb-4 text-xs text-green-800 dark:text-green-300">
            <AlertTriangle size={12} className="inline-block mr-1" />
            {paymentMethods.bankTransfer?.additionalInfo}
          </div>
        )}
      </div>

      {/* Platform Name using SearchableSelect */}
      <div className="p-4 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-950/50 rounded-xl border border-neutral-200 dark:bg-neutral-800">
        <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
          {t("Platform Name")}
        </label>
        <SearchableSelect
          options={PLATFORM_OPTIONS}
          value={platformName}
          onChange={(val) => setPlatformName(val)}
          placeholder={t("Select bank/platform")}
          icon={<Wallet size={16} />}
        />
        {platformName === "Other" && (
          <div className="mt-2">
            <InputCustom
              label={t("Enter bank/platform name")}
              placeholder={t("Type the bank/platform name")}
              value={customPlatformName}
              onChange={setCustomPlatformName}
              icon={<Building size={16} />}

            />
          </div>
        )}
      </div>

      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
        <h4 className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-2">
          {t("Upload Payment Receipt")}
        </h4>
        <p className="text-xs text-blue-600 dark:text-blue-300 mb-2">
          {t("Please upload here your original PDF proof of payment issued by your bank.")}
        </p>

        <FileDropZone
          file={receiptFile}
          setFile={setReceiptFile}
          allowedTypes={["application/pdf"]}
          maxSizeMB={20}
        />

        {/* Confirmations */}
        <div className="mt-4 space-y-2 text-xs text-neutral-700 dark:text-neutral-300">
          <p className="font-medium">
            {t("Please confirm before submitting that you have:")}
          </p>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={confirmations.reference}
              onChange={(e) => setConfirmations((c) => ({ ...c, reference: e.target.checked }))}
            />
            {t("Included your")} <span className="font-semibold">Client ID</span> {t("in the payment")}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={confirmations.wireTransfer}
              onChange={(e) => setConfirmations((c) => ({ ...c, wireTransfer: e.target.checked }))}
            />
            {t("Sent the payment via")} <span className="font-semibold">Wire Transfer</span> {t("(for faster arrival)")}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={confirmations.fees}
              onChange={(e) => setConfirmations((c) => ({ ...c, fees: e.target.checked }))}
            />
            {t("Included your")} <span className="font-semibold">Fees</span> {t("with the payment")}
          </label>
        </div>

        <button
          onClick={handleSubmitPayment}
          disabled={
            !receiptFile ||
            isProcessing ||
            !allConfirmed ||
            (platformName === "Other" && !customPlatformName.trim())
          }
          className="w-full mt-4 px-4 py-2.5 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <Loader2 size={14} className="animate-spin mr-2" />
              {t("Processing...")}
            </>
          ) : (
            t("Submit Bank Transfer Receipt")
          )}
        </button>
      </div>
    </div>
  )
}

export default BankTransferPayment
